/* Las frases en japonés: en lista, en grande para enseñarlas y leídas en voz alta por el móvil.
   Las favoritas (★) suben arriba; se guardan en este móvil. */
import { useRef, useState } from 'react';
import { borrarFrase, guardarFrase, misFrases, useApuntes } from '../datos/apuntes.ts';
import { noAutorizado } from '../datos/sesion.ts';
import { escuchar, hayVoz } from '../datos/voz.ts';
import { Constructor } from './Constructor.tsx';
import { FRASES, leer, situacionesDe } from '../datos/frases.ts';
import type { Frase, Viaje } from '../datos/tipos.ts';

const CLAVE_FAV = 'tabiji.frases.favoritas';
const leerFav = (): string[] => { try { return JSON.parse(localStorage.getItem(CLAVE_FAV) ?? '[]'); } catch { return []; } };

function useFavoritas() {
  const [fav, setFav] = useState<string[]>(leerFav);
  const alternar = (ja: string) => setFav(f => {
    const n = f.includes(ja) ? f.filter(x => x !== ja) : [...f, ja];
    try { localStorage.setItem(CLAVE_FAV, JSON.stringify(n)); } catch { /* sin sitio: solo esta vez */ }
    return n;
  });
  return [fav, alternar] as const;
}

export const MIAS = 'Mis frases';
/** Las del autor (traducidas por él) primero, luego las de la app y las de los documentos. */
export const todasLasFrases = (v: Viaje): Frase[] => [
  ...misFrases().map(f => ({ situacion: MIAS, es: f.es, ja: f.ja, romaji: f.romaji ?? '', id: f.id })),
  ...FRASES, ...v.frases,
];

/** La frase a pantalla completa, para enseñársela a quien sea, con su botón de escuchar. */
export function FraseGrande({ f, onCerrar }: { f: Frase; onCerrar: () => void }) {
  return (
    <div className="frase-grande" role="dialog" aria-label={f.es}>
      <span lang="ja">{f.ja}</span>
      <small>{f.romaji}</small>
      <small>{f.es}</small>
      <div className="frase-grande__acciones">
        <button type="button" className="accion" onClick={() => leer(f)}>🔊 Escuchar</button>
        <button type="button" className="accion" onClick={onCerrar}>Cerrar</button>
      </div>
    </div>
  );
}

function Fila({ f, fav, onFav, onAbrir, onBorrar }: { f: Frase; fav?: boolean; onFav?: () => void; onAbrir: () => void; onBorrar?: () => void }) {
  return (
    <div className="frase-fila">
      <button type="button" className="frase" onClick={onAbrir}>
        <span className="frase__es">{f.es}</span>
        <span className="frase__ja" lang="ja">{f.ja}</span>
        <span className="frase__romaji">{f.romaji}</span>
      </button>
      <button type="button" className="frase__oir" aria-label={`Escuchar «${f.es}»`} onClick={() => leer(f)}>🔊</button>
      {onBorrar
        ? <button type="button" className="frase__fav" aria-label={`Borrar «${f.es}»`} onClick={() => { if (confirm(`¿Borrar «${f.es}» de tus frases?`)) onBorrar(); }}>✕</button>
        : onFav && <button type="button" className="frase__fav" aria-pressed={fav} aria-label={fav ? 'Quitar de favoritas' : 'Marcar como favorita'} onClick={onFav}>{fav ? '★' : '☆'}</button>}
    </div>
  );
}

/** Guía → Frases: atajos por situación, las favoritas arriba y todas debajo. */
export function FrasesGuia({ v }: { v: Viaje }) {
  useApuntes();
  const [grande, setGrande] = useState<Frase | null>(null);
  const [fav, alternar] = useFavoritas();
  const todas = todasLasFrases(v);
  const situaciones = [...new Set(todas.map(f => f.situacion))];
  const [sit, setSit] = useState<string | null>(null);
  const favoritas = todas.filter(f => fav.includes(f.ja));
  return (
    <>
      <Traductor onGrande={setGrande} />
      <Constructor onGrande={setGrande} />
      <div className="filtros chips-frases" role="group" aria-label="Situación">
        <button type="button" aria-pressed={sit === null} onClick={() => setSit(null)}>Todas</button>
        {situaciones.map(s => <button key={s} type="button" aria-pressed={sit === s} onClick={() => setSit(s)}>{s}</button>)}
      </div>
      {sit === null && favoritas.length > 0 && (
        <section className="doc-seccion">
          <h3>★ Favoritas</h3>
          {favoritas.map(f => <Fila key={f.ja} f={f} fav onFav={() => alternar(f.ja)} onAbrir={() => setGrande(f)} />)}
        </section>
      )}
      {situaciones.filter(s => sit === null || s === sit).map(s => (
        <section key={s} className="doc-seccion">
          <h3>{s}</h3>
          {todas.filter(f => f.situacion === s).map(f => <Fila key={f.id ?? f.ja} f={f} fav={fav.includes(f.ja)} onFav={() => alternar(f.ja)} onAbrir={() => setGrande(f)} onBorrar={f.id ? () => borrarFrase(f.id!) : undefined} />)}
        </section>
      ))}
      {sit === null && favoritas.length === 0 && <p className="texto" style={{ fontSize: '.85rem' }}>Marca con ☆ las que más uses y saldrán arriba.</p>}
      {grande && <FraseGrande f={grande} onCerrar={() => setGrande(null)} />}
    </>
  );
}

/** En la hoja de una parada: las frases que vienen a cuento ahí, a un toque. */
export function FrasesAqui({ v, hito, tipo, que, onTodas }: { v: Viaje; hito: string | null; tipo: string; que: string; onTodas: () => void }) {
  const [grande, setGrande] = useState<Frase | null>(null);
  const fav = leerFav();
  const sits = situacionesDe(hito, tipo, que);
  const todas = todasLasFrases(v).filter(f => sits.includes(f.situacion));
  // Primero las favoritas de esas situaciones; luego las primeras de cada una
  const elegidas = [...todas.filter(f => fav.includes(f.ja)), ...todas].filter((f, i, a) => a.indexOf(f) === i).slice(0, 4);
  return (
    <details className="plegable">
      <summary>Frases para aquí · {sits[0].toLowerCase()}</summary>
      <div>
        {elegidas.map(f => <Fila key={f.ja} f={f} onAbrir={() => setGrande(f)} />)}
        <p><button type="button" className="enlace-texto" onClick={onTodas}>Todas las frases ({todasLasFrases(v).length})</button></p>
      </div>
      {grande && <FraseGrande f={grande} onCerrar={() => setGrande(null)} />}
    </details>
  );
}

/** Escribir una frase y traducirla (con red: la traduce la Pi). Español → japonés con su pronunciación, o al revés
    para entender una respuesta o un cartel. Lo traducido se puede guardar en «Mis frases». */
function Traductor({ onGrande }: { onGrande: (f: Frase) => void }) {
  const [texto, setTexto] = useState('');
  const [sentido, setSentido] = useState<'es-ja' | 'ja-es'>('es-ja');
  const [estado, setEstado] = useState<'listo' | 'traduciendo' | 'error'>('listo');
  const [error, setError] = useState('');
  const [res, setRes] = useState<{ origen: string; texto: string; romaji: string | null; sentido: string } | null>(null);
  const [guardada, setGuardada] = useState(false);
  const [oyendo, setOyendo] = useState<null | 'es-ja' | 'ja-es'>(null);
  const corte = useRef<() => void>(() => {});

  /** Hablas (en español) o habla él (en japonés): se escucha, se traduce y, si hablas tú, se lee el japonés en voz alta. */
  const hablar = async (que: 'es-ja' | 'ja-es') => {
    setSentido(que); setRes(null); setEstado('listo'); setOyendo(que);
    const e = escuchar(que === 'es-ja' ? 'es-ES' : 'ja-JP');
    corte.current = e.parar;
    try {
      const dicho = await e.texto;
      setTexto(dicho);
      await traducir(dicho, que, que === 'es-ja');
    } catch (x) { setEstado('error'); setError(x instanceof Error ? x.message : 'No se pudo escuchar'); }
    finally { setOyendo(null); }
  };

  const traducir = async (dicho?: string, sent = sentido, leerAlto = false) => {
    const q = (dicho ?? texto).trim();
    if (!q) return;
    if (!navigator.onLine) { setEstado('error'); setError('Sin red no se puede traducir. Tus frases guardadas y el constructor sí funcionan.'); return; }
    setEstado('traduciendo'); setGuardada(false);
    try {
      const r = await fetch(`/api/traducir?sentido=${sent}&q=${encodeURIComponent(q)}`);
      const d = await r.json();
      if (r.status === 401) noAutorizado();
      if (!r.ok) throw new Error(d.error ?? 'No se pudo traducir');
      setRes(d); setEstado('listo');
      if (leerAlto) leer({ situacion: MIAS, es: d.origen, ja: d.texto, romaji: d.romaji ?? '' });
    } catch (e) { setEstado('error'); setError(e instanceof Error ? e.message : 'No se pudo traducir'); }
  };
  const comoFrase = (): Frase | null => res && (res.sentido === 'es-ja'
    ? { situacion: MIAS, es: res.origen, ja: res.texto, romaji: res.romaji ?? '' }
    : { situacion: MIAS, es: res.texto, ja: res.origen, romaji: res.romaji ?? '' });
  return (
    <section className="traductor" aria-label="Traducir una frase">
      {hayVoz()
        ? (
          <div className="conversacion" role="group" aria-label="Conversar con voz">
            {oyendo
              ? <button type="button" className="mic mic--oyendo" onClick={() => corte.current()}>🎤 Escuchando{oyendo === 'es-ja' ? ' (habla en español)' : ' (que hable en japonés)'}… toca para terminar</button>
              : <>
                  <button type="button" className="mic" onClick={() => void hablar('es-ja')}>🎤 Hablo yo<small>español → 日本語 y lo lee en voz alta</small></button>
                  <button type="button" className="mic" onClick={() => void hablar('ja-es')}>🎤 Habla él<small>日本語 → español</small></button>
                </>}
          </div>)
        : <p className="texto" style={{ fontSize: '.85rem' }}>Para hablar y traducir hace falta Chrome en Android (este navegador no tiene reconocimiento de voz).</p>}
      <div className="filtros" role="group" aria-label="Sentido">
        <button type="button" aria-pressed={sentido === 'es-ja'} onClick={() => { setSentido('es-ja'); setRes(null); }}>Español → 日本語</button>
        <button type="button" aria-pressed={sentido === 'ja-es'} onClick={() => { setSentido('ja-es'); setRes(null); }}>日本語 → Español</button>
      </div>
      <textarea className="nota" value={texto} maxLength={300} lang={sentido === 'ja-es' ? 'ja' : 'es'}
        placeholder={sentido === 'es-ja' ? 'Escribe lo que quieras decir: «¿Tienen mesa para tres en la barra?»' : 'Pega o escribe el japonés que quieras entender'}
        onChange={e => setTexto(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void traducir(); } }} />
      <button type="button" className="accion accion--ancha" disabled={!texto.trim() || estado === 'traduciendo'} onClick={() => void traducir()}>
        {estado === 'traduciendo' ? 'Traduciendo…' : '訳 Traducir lo escrito'}
      </button>
      {estado === 'error' && <p className="aviso" role="status">{error}</p>}
      {res && (() => {
        const f = comoFrase()!;
        return (
          <div className="traduccion" role="status">
            {res.sentido === 'es-ja'
              ? <><span className="frase__ja" lang="ja">{res.texto}</span>{res.romaji && <span className="frase__romaji">{res.romaji}</span>}</>
              : <><span className="frase__es traduccion__es">{res.texto}</span>{res.romaji && <span className="frase__romaji">{res.romaji}</span>}</>}
            <div className="traduccion__acciones">
              <button type="button" className="accion" onClick={() => leer(f)}>🔊 Escuchar</button>
              <button type="button" className="accion" onClick={() => onGrande(f)}>Ver en grande</button>
              <button type="button" className="accion" disabled={guardada} onClick={() => { guardarFrase({ es: f.es, ja: f.ja, romaji: f.romaji || null }); setGuardada(true); }}>{guardada ? '✓ Guardada' : '＋ Guardar'}</button>
            </div>
            <small className="traduccion__fuente">Traducción automática (MyMemory): para cosas importantes, mejor con frases sencillas.</small>
          </div>
        );
      })()}
    </section>
  );
}
